import { schema, t } from '@colyseus/schema'
import type { PlayerState } from '@runkr/sim'

/**
 * One racer on the wire (ARCHITECTURE §4.2, PHASE1 T1.6): every `PlayerState` field, so the
 * client can reconcile its own runner and draw everyone, plus room data. `index` is the sim
 * player index (`PlayerState.id`). Nothing marks bots: they get the same fields and a
 * session-id-like key. Defined with the decorator-free `schema()` builder (D-026); 43 fields
 * (Colyseus allows 64 per class).
 */
export const PlayerSchema = schema(
  {
    index: t.number().default(0),
    displayName: t.string().default(''),
    /** Cosmetic character; the index until characters exist (D-031). */
    charId: t.number().default(0),
    /** Profile level (D-053); 1 for guests and dev mode, near the humans' for bots. */
    level: t.number().default(1),
    /** False while the client is away (reconnect window) or gone. Bots are always true. */
    connected: t.boolean().default(true),
    // PlayerState (sim), same names and units. Fractional fields are float64: `t.number()`
    // sends floats as float32, and client reconciliation needs the exact server value.
    x: t.float64().default(0),
    y: t.float64().default(0),
    vx: t.float64().default(0),
    vy: t.float64().default(0),
    w: t.float64().default(0),
    h: t.float64().default(0),
    grounded: t.boolean().default(false),
    jumpsLeft: t.number().default(0),
    coyoteUntil: t.number().default(0),
    jumpBufferedUntil: t.number().default(0),
    jumpHeldTicks: t.number().default(0),
    stunnedUntil: t.number().default(0),
    invulnUntil: t.number().default(0),
    shieldUntil: t.number().default(0),
    boostUntil: t.number().default(0),
    /** `ItemId`, or '' for an empty slot. */
    item: t.string().default(''),
    itemCooldownUntil: t.number().default(0),
    checkpoint: t.number().default(0),
    respawnAtTick: t.number().default(0),
    progress: t.float64().default(0),
    placement: t.number().default(0),
    finishTick: t.number().default(0),
    rank: t.number().default(0),
    boostPct: t.float64().default(0),
    boostIgnoresMud: t.boolean().default(false),
    coins: t.number().default(0),
    lastHitTick: t.number().default(0),
    /** `RideId`, or '' for none (T3.10). */
    rideId: t.string().default(''),
    rideCharge: t.number().default(0),
    summonAtTick: t.number().default(0),
    ridingUntil: t.number().default(0),
    burrowUntil: t.number().default(0),
    bumpedMask: t.number().default(0),
    skidUntil: t.number().default(0),
    magnetUntil: t.number().default(0),
    /** Tick of the last input the server applied for this player (client reconciliation). */
    lastInputTick: t.number().default(0),
    prevBits: t.number().default(0),
  },
  'PlayerSchema',
)
export type PlayerSchema = InstanceType<typeof PlayerSchema>

/** Numeric `PlayerState` fields copied one to one. */
export const PLAYER_NUMBER_KEYS = [
  'x',
  'y',
  'vx',
  'vy',
  'w',
  'h',
  'jumpsLeft',
  'coyoteUntil',
  'jumpBufferedUntil',
  'jumpHeldTicks',
  'stunnedUntil',
  'invulnUntil',
  'shieldUntil',
  'boostUntil',
  'itemCooldownUntil',
  'checkpoint',
  'respawnAtTick',
  'progress',
  'placement',
  'finishTick',
  'rank',
  'boostPct',
  'coins',
  'lastHitTick',
  'rideCharge',
  'summonAtTick',
  'ridingUntil',
  'burrowUntil',
  'bumpedMask',
  'skidUntil',
  'magnetUntil',
  'lastInputTick',
  'prevBits',
] as const satisfies readonly (keyof PlayerState & keyof PlayerSchema)[]

/** Boolean `PlayerState` fields copied one to one. */
export const PLAYER_BOOL_KEYS = [
  'grounded',
  'boostIgnoresMud',
] as const satisfies readonly (keyof PlayerState & keyof PlayerSchema)[]

// Compile-time guard: a new `PlayerState` field must be added to the schema and a key list
// (`id` maps to `index`, `item` and `rideId` are mapped by hand).
type Unmapped = Exclude<
  keyof PlayerState,
  (typeof PLAYER_NUMBER_KEYS)[number] | (typeof PLAYER_BOOL_KEYS)[number] | 'id' | 'item' | 'rideId'
>
const allPlayerFieldsMapped: [Unmapped] extends [never] ? true : Unmapped = true
void allPlayerFieldsMapped
